import React from "react";
import Form from "react-bootstrap/esm/Form";
import InputGroup from "react-bootstrap/esm/InputGroup";
import FormControl from "react-bootstrap/esm/FormControl";
import FormLabel from "react-bootstrap/esm/FormLabel";
import { Col, Row } from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import DatePicker from "react-datepicker";
import { isAfter, isBefore, subYears } from "date-fns";
import { US_STATES } from "shared/States";
import { Constants } from "shared/Constants";
import InputValidators from "utilities/InputValidators";
import "./CustomerProfileForm.css";

interface CustomerProfileFormProps {
  mode: 'create' | 'edit';
  // Field values
  firstName?: string;
  lastName?: string;
  suffix?: string;
  dateOfBirth?: Date;
  businessName?: string;
  streetAddress?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phoneNumber?: string;
  farmingStartYear?: number;
  email?: string;
  // Create-only
  password?: string;
  confirmPassword?: string;
  termsOfUseAgree?: boolean;
  // Change handlers
  onFirstNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLastNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSuffixChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDOBChange: (date: Date) => void;
  onBusinessNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onStreetAddressChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onStreetAddress2Change: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onStateChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onZipCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPhoneChange: (value: string, data: any, event: any, formattedValue: string) => void;
  onFarmingStartYearChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  // Create-only handlers
  onPasswordChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onConfirmPasswordChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTermsAgreeChange?: (checked: boolean) => void;
}

export default function CustomerProfileForm(props: CustomerProfileFormProps) {
  const {
    mode,
    firstName, lastName, suffix, dateOfBirth, businessName,
    streetAddress, streetAddress2, city, state, zipCode,
    phoneNumber, farmingStartYear, email,
    password, confirmPassword, termsOfUseAgree,
    onFirstNameChange, onLastNameChange, onSuffixChange, onDOBChange,
    onBusinessNameChange, onStreetAddressChange, onStreetAddress2Change,
    onCityChange, onStateChange, onZipCodeChange, onPhoneChange,
    onFarmingStartYearChange, onEmailChange,
    onPasswordChange, onConfirmPasswordChange, onTermsAgreeChange,
  } = props;

  const currentYear = new Date().getFullYear();

  // Derive validation states — only "active" (show feedback) once the field has a value
  const hasEmail = (email ?? '').length > 0;
  const emailValid = hasEmail && InputValidators.IsValidEmailAddress(email ?? '');
  const emailInvalid = hasEmail && !emailValid;

  const hasFirstName = (firstName ?? '').length > 0;
  // No invalid state for first name — any non-empty value is fine

  const hasLastName = (lastName ?? '').length > 0;

  const hasDOB = dateOfBirth !== undefined;
  const dobValid = hasDOB
    && !isBefore(new Date(dateOfBirth!), subYears(new Date(), 125))
    && !isAfter(new Date(dateOfBirth!), new Date());
  const dobInvalid = hasDOB && !dobValid;

  const hasAddress = (streetAddress ?? '').length > 0;

  const hasCity = (city ?? '').length > 0;

  const hasState = (state ?? '').length > 0;

  const hasZip = (zipCode ?? '').length > 0;
  const zipValid = hasZip && InputValidators.IsValidZipCode(zipCode ?? '');
  const zipInvalid = hasZip && !zipValid;

  const phoneDigits = (phoneNumber ?? '').replace(/\D/g, '');
  const hasPhone = phoneDigits.length > 0;
  const phoneValid = phoneDigits.length === 10;
  const phoneInvalid = hasPhone && !phoneValid;

  const farmYearStr = String(farmingStartYear ?? '');
  const hasFarmYear = farmYearStr.length > 0 && farmingStartYear !== undefined;
  const farmYearValid = hasFarmYear
    && +farmingStartYear! > 0
    && +farmingStartYear! >= (currentYear - 125)
    && +farmingStartYear! <= currentYear;
  const farmYearInvalid = hasFarmYear && !farmYearValid;

  const hasPassword = (password ?? '').length > 0;
  const passwordValid = hasPassword && InputValidators.IsValidPassword(password ?? '');
  const passwordInvalid = hasPassword && !passwordValid;

  const hasConfirm = (confirmPassword ?? '').length > 0;
  const confirmValid = hasConfirm && passwordValid && confirmPassword === password;
  const confirmInvalid = hasConfirm && (!passwordValid || confirmPassword !== password);

  return (
    <Form>
      <Form.Group>
        <Row>
          <Form.Group as={Col}>
            <FormLabel>Email*{mode === 'edit' && <span style={{ fontWeight: 'normal', fontSize: '0.85em' }}> (This will change your login email)</span>}</FormLabel>
            <InputGroup className="mb-1">
              <FormControl
                placeholder="Email"
                aria-label="Email"
                type="text"
                value={email ?? ''}
                onChange={onEmailChange}
                isValid={emailValid}
                isInvalid={emailInvalid}
              />
              <Form.Control.Feedback type="invalid">Please enter a valid email address.</Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Row>
      </Form.Group>

      <Form.Group>
        <Row>
          <Form.Group as={Col}>
            <FormLabel>First Name*</FormLabel>
            <InputGroup className="mb-1">
              <FormControl
                placeholder="First Name"
                aria-label="First Name"
                value={firstName ?? ''}
                onChange={onFirstNameChange}
                isValid={hasFirstName}
              />
            </InputGroup>
          </Form.Group>

          <Form.Group as={Col}>
            <FormLabel>Last Name*</FormLabel>
            <InputGroup className="mb-1">
              <FormControl
                placeholder="Last Name"
                aria-label="Last Name"
                value={lastName ?? ''}
                onChange={onLastNameChange}
                isValid={hasLastName}
              />
            </InputGroup>
          </Form.Group>
        </Row>
      </Form.Group>

      <Form.Group>
        <Row>
          <Form.Group as={Col}>
            <FormLabel>Suffix</FormLabel>
            <InputGroup className="mb-3">
              <FormControl
                placeholder="Jr, Sr, III"
                aria-label="Suffix"
                value={suffix ?? ''}
                onChange={onSuffixChange}
              />
            </InputGroup>
          </Form.Group>

          <Form.Group as={Col}>
            <FormLabel>Date of Birth*</FormLabel>
            <div className="mb-1">
              <DatePicker
                selected={dateOfBirth != undefined ? new Date(dateOfBirth) : undefined}
                onChange={(value: Date) => onDOBChange(value)}
                className={`form-control${dobValid ? ' is-valid' : dobInvalid ? ' is-invalid' : ''}`}
              />
              {dobInvalid && <div className="invalid-feedback" style={{ display: 'block' }}>Please enter a valid date of birth.</div>}
            </div>
          </Form.Group>
        </Row>
      </Form.Group>

      <Form.Group>
        <Row>
          <Form.Group as={Col}>
            <FormLabel>Business Name</FormLabel>
            <InputGroup className="mb-3">
              <FormControl
                aria-label="Business Name"
                value={businessName ?? ''}
                onChange={onBusinessNameChange}
              />
            </InputGroup>
          </Form.Group>
        </Row>
      </Form.Group>

      <Form.Group>
        <Row>
          <Form.Group as={Col}>
            <FormLabel>Address Line 1*</FormLabel>
            <InputGroup className="mb-1">
              <FormControl
                aria-label="Address Line 1"
                value={streetAddress ?? ''}
                onChange={onStreetAddressChange}
                isValid={hasAddress}
              />
            </InputGroup>
          </Form.Group>

          <Form.Group as={Col}>
            <FormLabel>Address Line 2</FormLabel>
            <InputGroup className="mb-3">
              <FormControl
                aria-label="Address Line 2"
                value={streetAddress2 ?? ''}
                onChange={onStreetAddress2Change}
              />
            </InputGroup>
          </Form.Group>
        </Row>
      </Form.Group>

      <Form.Group>
        <Row>
          <Form.Group as={Col}>
            <FormLabel>City*</FormLabel>
            <InputGroup className="mb-1">
              <FormControl
                aria-label="City"
                value={city ?? ''}
                onChange={onCityChange}
                isValid={hasCity}
              />
            </InputGroup>
          </Form.Group>
        </Row>
      </Form.Group>

      <Form.Group>
        <Row>
          <Form.Group as={Col}>
            <FormLabel>State*</FormLabel>
            <FormControl
              as="select"
              aria-label="State"
              value={state ?? ''}
              onChange={onStateChange as any}
              className={`mb-1${hasState ? ' is-valid' : ''}`}
            >
              <option value="">Select State</option>
              {US_STATES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </FormControl>
          </Form.Group>

          <Form.Group as={Col}>
            <FormLabel>Zip Code*</FormLabel>
            <InputGroup className="mb-1">
              <FormControl
                aria-label="Zip Code"
                value={zipCode ?? ''}
                onChange={onZipCodeChange}
                isValid={zipValid}
                isInvalid={zipInvalid}
              />
              <Form.Control.Feedback type="invalid">Please enter a valid 5-digit zip code.</Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Row>
      </Form.Group>

      <Form.Group>
        <Row>
          <Form.Group as={Col}>
            <Form.Group className="mb-1" controlId="formBasicPhoneNumber">
              <Form.Label id="customer-phone-number">Phone Number*</Form.Label>
              <PhoneInput
                specialLabel=""
                placeholder="(402) 123-4567"
                disableCountryCode={true}
                inputClass={`form-control${phoneValid ? ' is-valid' : phoneInvalid ? ' is-invalid' : ''}`}
                country={'us'}
                onlyCountries={['us']}
                value={`${phoneNumber?.toString() ?? ''}`}
                onChange={onPhoneChange}
              />
              {phoneInvalid && <div className="invalid-feedback" style={{ display: 'block' }}>Please enter a valid 10-digit phone number.</div>}
            </Form.Group>
          </Form.Group>

          <Form.Group as={Col}>
            <FormLabel>Year Managing Farm Start*</FormLabel>
            <FormControl
              type="number"
              placeholder="Enter Year"
              value={farmingStartYear ?? ''}
              onChange={onFarmingStartYearChange}
              isValid={farmYearValid}
              isInvalid={farmYearInvalid}
            />
            <Form.Control.Feedback type="invalid">
              Please enter a year between {currentYear - 100} and {currentYear}.
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
      </Form.Group>

      {mode === 'create' && (
        <>
          <Form.Group>
            <Row>
              <Form.Group as={Col}>
                <FormLabel>Password*</FormLabel>
                <FormControl
                  placeholder="Password"
                  aria-label="Password"
                  type="password"
                  value={password ?? ''}
                  onChange={onPasswordChange}
                  isValid={passwordValid}
                  isInvalid={passwordInvalid}
                  className="mb-1"
                />
                <Form.Control.Feedback type="valid">Valid Format</Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  Must be 8+ characters with uppercase, lowercase, number, and special character.
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group as={Col}>
                <FormLabel>Confirm Password*</FormLabel>
                <FormControl
                  placeholder="Confirm Password"
                  aria-label="Confirm Password"
                  type="password"
                  value={confirmPassword ?? ''}
                  onChange={onConfirmPasswordChange}
                  isValid={confirmValid}
                  isInvalid={confirmInvalid}
                  className="mb-1"
                />
                <Form.Control.Feedback type="valid">Passwords Match</Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  {!passwordValid ? 'Fix password requirements first.' : 'Passwords do not match.'}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              checked={termsOfUseAgree ?? false}
              onChange={(e) => onTermsAgreeChange && onTermsAgreeChange(e.target.checked)}
              label={
                <span>
                  I agree to the{' '}
                  <a href={Constants.PolicyLinks.TermsOfUse} target="_blank" rel="noreferrer">Terms of Use</a>.
                </span>
              }
            />
          </Form.Group>
        </>
      )}
    </Form>
  );
}
